import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../api/apiClient";
import type { AssistantChartSpec } from "../components/cashChat/AssistantChart";

export interface CashChatMessage {
  role: "user" | "assistant";
  content: string;
  /** Charts the assistant produced for this reply, if any. */
  charts?: AssistantChartSpec[];
}

export interface DashboardCard {
  id: number;
  title: string;
  spec: AssistantChartSpec;
  sourcePrompt: string | null;
  createdAt: string;
}

function parseChatError(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (detail && typeof detail === "object" && "error" in detail) {
    const message = (detail as { error?: unknown }).error;
    if (typeof message === "string") return message;
  }
  return "The assistant is unavailable right now. Please try again.";
}

export type CashChatDomain = "all" | "receivables";

export function useCashChat(options?: { domain?: CashChatDomain }) {
  const domain = options?.domain ?? "all";
  const [messages, setMessages] = useState<CashChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendMessage = useCallback(
    async (content: string) => {
      const trimmed = content.trim();
      if (!trimmed || isLoading) return;

      const history: CashChatMessage[] = [
        ...messages,
        { role: "user", content: trimmed },
      ];
      setMessages(history);
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiClient.post<{
          reply: string;
          charts?: AssistantChartSpec[];
        }>("/cash-chat", {
          // Only role/content go to the model; charts are display-only.
          messages: history.map(({ role, content: text }) => ({
            role,
            content: text,
          })),
          domain,
        });
        setMessages([
          ...history,
          {
            role: "assistant",
            content: response.data.reply,
            charts: response.data.charts ?? [],
          },
        ]);
      } catch (requestError) {
        setError(parseChatError(requestError));
        // Keep the user's message so they can retry by resending.
      } finally {
        setIsLoading(false);
      }
    },
    [messages, isLoading, domain],
  );

  const reset = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return { messages, isLoading, error, sendMessage, reset };
}

/** The user's pinned assistant charts. */
export function useDashboardCards(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const [cards, setCards] = useState<DashboardCard[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiClient.get<{ cards: DashboardCard[] }>(
        "/assistant/cards",
      );
      setCards(response.data.cards ?? []);
      setError(null);
    } catch (requestError) {
      setError(parseChatError(requestError));
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const pinCard = useCallback(
    async (input: {
      title: string;
      spec: AssistantChartSpec;
      sourcePrompt?: string | null;
    }) => {
      if (!enabled) {
        throw new Error("Chart pinning is not available here.");
      }
      const response = await apiClient.post<DashboardCard>("/assistant/cards", {
        title: input.title,
        spec: input.spec,
        source_prompt: input.sourcePrompt ?? null,
      });
      setCards((previous) => [...previous, response.data]);
      return response.data;
    },
    [enabled],
  );

  const removeCard = useCallback(
    async (cardId: number) => {
      if (!enabled) return;
      await apiClient.delete(`/assistant/cards/${cardId}`);
      setCards((previous) => previous.filter((card) => card.id !== cardId));
    },
    [enabled],
  );

  return { cards, isLoading, error, refresh, pinCard, removeCard };
}
